import { EditOutlined, UndoOutlined } from '@ant-design/icons'
import {
  Alert,
  Button,
  Descriptions,
  Flex,
  Result,
  Space,
  Tag,
  Typography,
} from 'antd'
import { toTitleCase } from 'features/dynamicOntology'
import { getEnumColorMap } from 'features/libra/ontology/enumColors'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldCategory,
  FieldValues,
  InputField,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import {
  ComputedSection,
  getComputedOutcomeLabels,
} from 'features/libra/sections/computedSection/computedSectionTypes'
import { StyledCard } from 'features/libra/stepComponents/niiReport/cards/cardsStyles'
import {
  DynamicFieldTooltip,
  TooltipConfig,
} from 'features/libra/stepComponents/niiScreening/tooltips/DynamicFieldTooltip'
import type { FieldValuesSetter } from 'features/libra/ticket/fieldOrigin'
import { useGeckoPermissions } from 'features/permissions'
import React, { FC, useCallback, useMemo, useState } from 'react'
import type { RolodexPermissionsValues } from 'types'

import { ComputedOutcomeModal } from './ComputedOutcomeModal'
import {
  isTriStateFunction,
  isWeightedScoringFunction,
} from './computeFunctionRegistry'
import { EligibilityGateView } from './EligibilityGateView'
import { EligibilityOverrideModal } from './EligibilityOverrideModal'
import {
  applyComputedSectionOverride,
  createParameterSnapshot,
} from './evaluateComputedSection'
import { useComputedSectionValue } from './useComputedSectionValue'

type Tri = 'low' | 'med' | 'high'

function isTriValue(value: unknown): value is Tri {
  return value === 'low' || value === 'med' || value === 'high'
}

function renderFieldValue(value: unknown): React.ReactNode {
  if (isTriValue(value)) {
    const colorMap = getEnumColorMap()
    return <Tag color={colorMap[value]}>{toTitleCase(value)}</Tag>
  }

  if (typeof value === 'boolean') {
    return value ? <Tag color='green'>Yes</Tag> : <Tag color='red'>No</Tag>
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
  }

  return '—'
}

interface ComputedSectionViewProps {
  config: ComputedSection
  fieldValues: FieldValues
  ontologyFields: Record<string, OntologyField>
  setFieldValues?: FieldValuesSetter
  context?: Record<string, unknown>
  readonly?: boolean
}

/**
 * A generic component for rendering computed sections.
 * Displays the input fields, computes the outcome, and allows manual override.
 */
export const ComputedSectionView: FC<ComputedSectionViewProps> = ({
  config,
  fieldValues,
  ontologyFields,
  setFieldValues,
  context,
  readonly: readonlyProp,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { user, hasPermissions } = useGeckoPermissions()

  const {
    computedResult,
    isOverridden,
    overrideValue,
    finalResult,
    overrideJustification,
    overrideActor,
    overrideTimestamp,
    displayFieldValues,
  } = useComputedSectionValue(config, fieldValues, context)

  const currentParameterSnapshot = useMemo(
    () => createParameterSnapshot(config, fieldValues),
    [config, fieldValues]
  )

  const hasOverridePermission = useMemo(() => {
    if (!config.overridePermissions?.length) return true
    return hasPermissions(
      config.overridePermissions as RolodexPermissionsValues[]
    )
  }, [config.overridePermissions, hasPermissions])

  const canUpdateOverride =
    !readonlyProp &&
    !!setFieldValues &&
    !!config.allowOverride &&
    hasOverridePermission
  const saveOverride = useCallback(
    (outcome?: boolean, justification?: string) => {
      if (!setFieldValues) return

      const actor = user?.email ?? user?.name ?? 'Unknown user'
      const timestamp = new Date().toISOString()
      const parameterSnapshot = currentParameterSnapshot

      setFieldValues((prev) =>
        applyComputedSectionOverride(config, prev, {
          outcome,
          justification,
          computedResult,
          actor,
          timestamp,
          parameterSnapshot,
        })
      )
    },
    [
      computedResult,
      config,
      currentParameterSnapshot,
      setFieldValues,
      user?.email,
      user?.name,
    ]
  )

  const title = config.title ?? 'Outcome'
  const isEligibilityGate = config.presentation?.variant === 'eligibility-gate'
  const canEdit = canUpdateOverride && computedResult !== undefined
  const canRevert = canUpdateOverride && isOverridden
  const { trueLabel, falseLabel } = getComputedOutcomeLabels(config)
  const incompleteLabel = config.presentation?.incompleteLabel ?? 'Incomplete'
  const triggerValue = config.presentation?.triggerValue ?? true

  const computationBreakdown = useMemo(() => {
    const functionSpec = config.functionSpec

    if (isWeightedScoringFunction(config.function)) {
      // Weighted boolean scoring breakdown. `threshold` may be 0 (fail on any
      // 'bad' answer), so guard on `undefined` rather than falsiness.
      if (functionSpec.threshold === undefined) return null

      let totalScore = 0
      const fieldScores: Array<{
        fieldId: string
        name: string
        value: boolean | undefined
        weight: number
        inverted: boolean
        contributes: boolean
        score: number
      }> = []

      const weightedParams = functionSpec.parameters.filter(
        (p) => 'weight' in p
      ) as Array<{
        field: string
        weight: number
        inverted?: boolean
      }>

      for (const paramSpec of weightedParams) {
        const value = unwrapFieldValue<boolean>(fieldValues[paramSpec.field])
        const ontologyField = ontologyFields[paramSpec.field]
        const name = ontologyField?.name || paramSpec.field
        const weight = paramSpec.weight
        const inverted = !!paramSpec.inverted
        const shouldScore = inverted ? !value : value
        const contributes = !!shouldScore && value !== undefined
        const score = contributes ? weight : 0

        totalScore += score

        fieldScores.push({
          fieldId: paramSpec.field,
          name,
          value,
          weight,
          inverted,
          contributes,
          score,
        })
      }

      return {
        type: 'weighted' as const,
        fieldScores,
        totalScore,
        threshold: functionSpec.threshold,
        result: totalScore <= functionSpec.threshold,
      }
    }

    if (isTriStateFunction(config.function)) {
      const triStateParams = functionSpec.parameters.filter(
        (p) => 'type' in p && p.type === 'tri-state'
      ) as Array<{
        field: string
        type: 'tri-state'
        defaultValue?: string
      }>

      const parameterValues: Array<{
        fieldId: string
        name: string
        value: 'low' | 'med' | 'high'
      }> = []

      for (const paramSpec of triStateParams) {
        const value = (unwrapFieldValue<string>(fieldValues[paramSpec.field]) ??
          paramSpec.defaultValue ??
          'low') as 'low' | 'med' | 'high'
        const ontologyField = ontologyFields[paramSpec.field]
        const name = ontologyField?.name || paramSpec.field

        parameterValues.push({
          fieldId: paramSpec.field,
          name,
          value,
        })
      }

      return {
        type: 'tristate' as const,
        parameterValues,
        result: computedResult ?? false,
      }
    }

    return null
  }, [config, fieldValues, ontologyFields, computedResult])

  // Get display fields with their labels, tooltips, and parameter info
  // Auto-derive display fields from functionSpec if displayFields not specified or empty
  const fieldsToDisplay =
    config.displayFields.length > 0
      ? config.displayFields
      : config.functionSpec.parameters.map((p) => p.field)

  const displayItems = fieldsToDisplay.map((fieldId) => {
    const ontologyField = ontologyFields[fieldId]
    const label = ontologyField?.name ?? fieldId
    const value = displayFieldValues[fieldId]
    // Get tooltip configuration if field is an input field
    const inputField =
      ontologyField?.category === FieldCategory.INPUT
        ? (ontologyField as InputField)
        : undefined
    const tooltipConfig = inputField?.tooltip

    // Get parameter info from functionSpec
    const paramSpec = config.functionSpec.parameters.find(
      (p) => p.field === fieldId
    )
    let parameterType: 'weighted' | 'tristate' | 'unknown' = 'unknown'
    let weight = 0
    let inverted = false

    if (paramSpec) {
      if ('weight' in paramSpec) {
        parameterType = 'weighted'
        weight = paramSpec.weight
        inverted = !!paramSpec.inverted
      } else if ('type' in paramSpec && paramSpec.type === 'tri-state') {
        parameterType = 'tristate'
        const ontologyField = ontologyFields[fieldId]
        inverted =
          ontologyField?.category === FieldCategory.INPUT
            ? !!ontologyField.invertedColors
            : false
      }
    }

    const shouldScore = inverted ? !value : value
    const contributes = !!shouldScore && value !== undefined
    const score = contributes ? weight : 0

    return {
      fieldId,
      label,
      value,
      tooltipConfig,
      parameterType,
      weight,
      inverted,
      contributes,
      score,
    }
  })

  // Render field label with optional tooltip and parameter info
  const renderFieldLabel = (
    label: string,
    tooltipConfig: TooltipConfig | undefined,
    parameterType: 'weighted' | 'tristate' | 'unknown',
    weight?: number
  ) => {
    const labelWithInfo = (
      <span>
        {label}
        {!isEligibilityGate &&
          parameterType === 'weighted' &&
          weight !== undefined && (
            <Typography.Text
              type='secondary'
              style={{
                fontSize: '11px',
                marginLeft: '8px',
                fontWeight: 500,
              }}>
              (Weight: {weight})
            </Typography.Text>
          )}
        {parameterType === 'tristate' && (
          <Typography.Text
            type='secondary'
            style={{
              fontSize: '11px',
              marginLeft: '8px',
              fontWeight: 500,
            }}>
            (Tri-state)
          </Typography.Text>
        )}
      </span>
    )

    if (tooltipConfig) {
      return (
        <Space size='small' align='start'>
          {labelWithInfo}
          <DynamicFieldTooltip tooltip={tooltipConfig} />
        </Space>
      )
    }

    return labelWithInfo
  }

  // Render field value with proper color coding based on parameter type
  const renderFieldValueWithScoring = (
    value: unknown,
    parameterType: 'weighted' | 'tristate' | 'unknown',
    inverted: boolean = false
  ) => {
    if (isEligibilityGate && typeof value !== 'boolean') {
      return <Tag>Not answered</Tag>
    }

    if (parameterType === 'tristate' && isTriValue(value)) {
      const colorMap = getEnumColorMap(inverted)
      return <Tag color={colorMap[value]}>{toTitleCase(value)}</Tag>
    }

    if (parameterType === 'weighted' && typeof value === 'boolean') {
      if (isEligibilityGate) {
        return (
          <Tag color={value === triggerValue ? 'gold' : 'default'}>
            {value ? 'Yes' : 'No'}
          </Tag>
        )
      }

      // For normal questions: Yes = bad (red), No = good (green)
      // For inverted questions: Yes = good (green), No = bad (red)
      const isGoodAnswer = inverted ? value : !value
      const color = isGoodAnswer ? 'success' : 'error'
      const text = value ? 'Yes' : 'No'

      return <Tag color={color}>{text}</Tag>
    }

    return renderFieldValue(value)
  }

  // Sub-component for displaying input field descriptions
  interface FieldDescriptionsProps {
    displayItems: Array<{
      fieldId: string
      label: string
      value: unknown
      tooltipConfig: TooltipConfig | undefined
      parameterType: 'weighted' | 'tristate' | 'unknown'
      weight: number
      inverted: boolean
    }>
    renderFieldLabel: (
      label: string,
      tooltipConfig: TooltipConfig | undefined,
      parameterType: 'weighted' | 'tristate' | 'unknown',
      weight?: number
    ) => React.ReactNode
    renderFieldValueWithScoring: (
      value: unknown,
      parameterType: 'weighted' | 'tristate' | 'unknown',
      inverted?: boolean
    ) => React.ReactNode
  }

  const FieldDescriptions: FC<FieldDescriptionsProps> = ({
    displayItems,
    renderFieldLabel,
    renderFieldValueWithScoring,
  }) => {
    if (displayItems.length === 0) return null

    return (
      <Descriptions column={1} bordered size='small'>
        {displayItems.map(
          ({
            fieldId,
            label,
            value,
            tooltipConfig,
            parameterType,
            weight,
            inverted,
          }) => (
            <Descriptions.Item
              key={fieldId}
              label={renderFieldLabel(
                label,
                tooltipConfig,
                parameterType,
                weight
              )}>
              {renderFieldValueWithScoring(value, parameterType, inverted)}
            </Descriptions.Item>
          )
        )}
      </Descriptions>
    )
  }

  interface OutcomeDisplayProps {
    finalResult: boolean | undefined
    canEdit: boolean
    canRevert: boolean
    onEditClick: () => void
    onClearOverride: () => void
    isOverridden: boolean
    overrideJustification?: string
  }

  const OutcomeDisplay: FC<OutcomeDisplayProps> = ({
    finalResult,
    canEdit,
    canRevert,
    onEditClick,
    onClearOverride,
    isOverridden,
    overrideJustification,
  }) => {
    const result = finalResult ?? false
    const actions = [
      ...(canEdit
        ? [
            <Button key='edit' icon={<EditOutlined />} onClick={onEditClick}>
              {isOverridden ? 'Edit override' : 'Override outcome'}
            </Button>,
          ]
        : []),
      ...(canRevert
        ? [
            <Button
              key='clear'
              icon={<UndoOutlined />}
              onClick={onClearOverride}>
              Revert to system result
            </Button>,
          ]
        : []),
    ]

    return (
      <Flex vertical gap='middle'>
        <Result
          status={result ? 'success' : 'error'}
          title={result ? 'Pass' : 'Fail'}
          extra={actions.length > 0 ? actions : undefined}
        />
        {isOverridden && overrideJustification && (
          <Alert
            type='warning'
            showIcon
            message={`Overridden: ${overrideJustification}`}
          />
        )}
      </Flex>
    )
  }

  interface ComputationBreakdownProps {
    breakdown:
      | {
          type: 'weighted'
          fieldScores: Array<{
            fieldId: string
            name: string
            value: boolean | undefined
            score: number
          }>
          totalScore: number
          threshold: number
          result: boolean
        }
      | {
          type: 'tristate'
          parameterValues: Array<{
            fieldId: string
            name: string
            value: 'low' | 'med' | 'high'
          }>
          result: boolean
        }
      | null
  }

  const ComputationBreakdown: FC<ComputationBreakdownProps> = ({
    breakdown,
  }) => {
    if (!breakdown) return null

    return (
      <Space direction='vertical' align='center' style={{ marginTop: '16px' }}>
        {breakdown.type === 'weighted' && (
          <Space>
            <Typography.Text strong>Total Score: </Typography.Text>
            <Tag
              color={
                breakdown.totalScore > breakdown.threshold ? 'red' : 'green'
              }
              style={{ fontSize: '14px', padding: '4px 8px' }}>
              {breakdown.totalScore}
            </Tag>
            <Typography.Text strong>Threshold: </Typography.Text>
            <Tag color='blue' style={{ fontSize: '14px', padding: '4px 8px' }}>
              ≤ {breakdown.threshold}
            </Tag>
          </Space>
        )}
        {breakdown.type === 'tristate' && (
          <Space direction='vertical' align='center'>
            <Typography.Text strong>Screening Decision Matrix</Typography.Text>
            <Typography.Text
              type='secondary'
              style={{ fontSize: '12px', textAlign: 'center' }}>
              Based on tri-state parameter combination
            </Typography.Text>
          </Space>
        )}
      </Space>
    )
  }

  return (
    <StyledCard title={title}>
      {isEligibilityGate ? (
        <Flex wrap gap='middle' align='stretch'>
          <Flex vertical flex='1 1 17rem'>
            <FieldDescriptions
              displayItems={displayItems}
              renderFieldLabel={renderFieldLabel}
              renderFieldValueWithScoring={renderFieldValueWithScoring}
            />
          </Flex>
          <Flex vertical flex='1 1 15rem'>
            <EligibilityGateView
              computedResult={computedResult}
              finalResult={finalResult}
              isOverridden={isOverridden}
              overrideJustification={overrideJustification}
              overrideActor={overrideActor}
              overrideTimestamp={overrideTimestamp}
              trueLabel={trueLabel}
              falseLabel={falseLabel}
              incompleteLabel={incompleteLabel}
              canEdit={canEdit}
              canRevert={canRevert}
              onEdit={() => setIsModalOpen(true)}
              onClearOverride={() => saveOverride()}
            />
          </Flex>
        </Flex>
      ) : (
        <Flex gap='middle' align='stretch'>
          <Flex vertical flex={1}>
            <FieldDescriptions
              displayItems={displayItems}
              renderFieldLabel={renderFieldLabel}
              renderFieldValueWithScoring={renderFieldValueWithScoring}
            />
          </Flex>
          <Flex vertical flex={1} align='center' justify='center' gap='middle'>
            <OutcomeDisplay
              finalResult={finalResult}
              canEdit={canEdit}
              canRevert={canRevert}
              onEditClick={() => setIsModalOpen(true)}
              onClearOverride={() => saveOverride()}
              isOverridden={isOverridden}
              overrideJustification={overrideJustification}
            />
            {!config.presentation?.hideScoring && (
              <ComputationBreakdown breakdown={computationBreakdown} />
            )}
          </Flex>
        </Flex>
      )}

      {config.allowOverride &&
        canUpdateOverride &&
        computedResult !== undefined &&
        isModalOpen &&
        (isEligibilityGate ? (
          <EligibilityOverrideModal
            computedOutcome={computedResult}
            currentJustification={overrideJustification}
            onClose={() => setIsModalOpen(false)}
            onSave={saveOverride}
            trueLabel={trueLabel}
            falseLabel={falseLabel}
            title={isOverridden ? 'Edit override' : 'Override decision'}
          />
        ) : (
          <ComputedOutcomeModal
            onClose={() => setIsModalOpen(false)}
            computedOutcome={computedResult}
            currentOverride={overrideValue}
            currentJustification={overrideJustification}
            onSave={saveOverride}
            title='Override decision'
            passLabel={trueLabel}
            failLabel={falseLabel}
          />
        ))}
    </StyledCard>
  )
}

export default React.memo(ComputedSectionView)
